import React, { createContext, useContext, useEffect, useState } from "react";
import {
  startSignalRConnection,
  registerNotificationCallback,
  registerUnreadCountCallback,
  stopSignalRConnection,
} from "../../services/signalRService";
import { get, put } from "../../services/api";
import { toast } from "react-toastify";

const NotificationContext = createContext();

// API functions (unchanged)
const markNotificationRead = async (notificationId, userId) => {
  try {
    await get(`AdminParentChat/mark-as-read/${notificationId}/${userId}`);
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

const markAllNotificationsRead = async (userId) => {
  try {
    await put(`notifications/mark-all-as-read/${userId}`);
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);

  // Initialize audio for notification sound (unchanged)
  const notificationSound = new Audio("/notification.mp3");

  const playNotificationSound = () => {
    if (!isSoundPlaying) {
      setIsSoundPlaying(true);
      notificationSound.play().catch((err) => {
        console.warn("Failed to play notification sound:", err);
        setIsSoundPlaying(false);
      });
      notificationSound.onended = () => {
        setIsSoundPlaying(false);
      };
    }
  };

  // Add storage listener and trigger state
  const [loginTrigger, setLoginTrigger] = useState(0);
  useEffect(() => {
    const handleStorageChange = () => {
      // Check if relevant keys now exist, regardless of which changed
      if (localStorage.getItem("userId") && localStorage.getItem("token")) {
        setLoginTrigger((prev) => prev + 1);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch initial notifications (read userId fresh)
  const fetchNotifications = async () => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) return;

    try {
      setLoading(true);
      const response = await get(
        `AdminParentChat/conversation/${currentUserId}`
      );
      setNotifications(response || []);
      const count = response.data?.filter((n) => !n.isRead).length || 0;
      setUnreadCount(count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Setup SignalR connection (read userId/token fresh, depend on trigger)
  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");
    if (!currentUserId || !token) {
      setConnectionStatus("disconnected");
      return;
    }

    let unsubscribeNotification;
    let unsubscribeUnreadCount;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const setupSignalR = async () => {
      try {
        setConnectionStatus("connecting");

        const connection = await startSignalRConnection(token);
        if (!connection) {
          throw new Error("Failed to establish connection, please refresh!");
        }

        const waitForConnection = async () => {
          let attempts = 0;
          while (connection.state !== "Connected" && attempts < 10) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            attempts++;
          }
          if (connection.state !== "Connected") {
            throw new Error("Connection timeout");
          }
        };

        await waitForConnection();

        const joinGroupWithRetry = async () => {
          try {
            await connection.invoke("JoinNotificationGroup", currentUserId);
          } catch (invokeError) {
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(
                `Retrying JoinNotificationGroup (attempt ${retryCount})`
              );
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * retryCount)
              );
              return joinGroupWithRetry();
            }
            throw invokeError;
          }
        };

        await joinGroupWithRetry();

        unsubscribeNotification = registerNotificationCallback(
          (notification) => {
            setNotifications((prev) => [notification, ...prev]);
            setUnreadCount((prev) => prev + 1);
            playNotificationSound();
            toast.info("You have a new notification", {
              position: "bottom-left",
            });
          }
        );

        unsubscribeUnreadCount = registerUnreadCountCallback((count) => {
          setUnreadCount(count);
        });

        connection.onclose(() => {
          setConnectionStatus("disconnected");
          if (retryCount < MAX_RETRIES) {
            setTimeout(setupSignalR, 2000);
          }
        });

        connection.onreconnecting(() => {
          setConnectionStatus("reconnecting");
        });

        connection.onreconnected(() => {
          setConnectionStatus("connected");
          connection
            .invoke("JoinNotificationGroup", currentUserId)
            .catch(console.error);
        });

        setConnectionStatus("connected");
        await fetchNotifications();
      } catch (err) {
        console.error("SignalR connection error:", err);
        setConnectionStatus("error");
        setError("Failed to establish real-time connection, please refresh!");

        if (retryCount < MAX_RETRIES) {
          retryCount++;
          console.log(`Retrying connection (attempt ${retryCount})`);
          setTimeout(setupSignalR, 2000 * retryCount);
        }
      }
    };

    setupSignalR();

    return () => {
      unsubscribeNotification?.();
      unsubscribeUnreadCount?.();
      stopSignalRConnection();
      setConnectionStatus("disconnected");
    };
  }, [loginTrigger]);

  // Cleanup audio event listener (unchanged)
  useEffect(() => {
    return () => {
      notificationSound.onended = null;
    };
  }, []);

  // Mark notification as read (read userId fresh)
  const markAsRead = async (notificationId) => {
    const currentUserId = localStorage.getItem("userId");
    try {
      await markNotificationRead(notificationId, currentUserId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Mark all notifications as read (read userId fresh)
  const markAllAsRead = async () => {
    const currentUserId = localStorage.getItem("userId");
    try {
      // await markAllNotificationsRead(currentUserId);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        connectionStatus,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
